import { useEffect, useState } from 'react';

import { DelayedSpinner } from '@/components/ui/delayed-spinner';
import { imagePathKey, probeCachedImage, recallPainted, rememberPainted } from '@/lib/imagePaint';

/** A detected box in frame-fraction (0..1) coords — same convention as
 *  cctv_core.models.BoundingBox (x0,y0 top-left, x1,y1 bottom-right). */
export interface FrameBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** false — бокс не попал в полигон (пересчёт очереди): рисуется серым пунктиром. */
  counted?: boolean;
}

/**
 * Renders a full camera frame with its target outline(s) so the polygon lines
 * up exactly with the (letterboxed) image: image + polygon live in ONE <svg>
 * whose viewBox is the frame's natural pixel size, with
 * preserveAspectRatio="meet" letterboxing both together. `polygons` are in
 * normalized 0..1 coords (PolygonArea.direction_vectors), scaled by the frame
 * dimensions. The <svg> is pinned absolute inside a bounded box so it always
 * fills its container and never falls back to its intrinsic (viewBox-ratio)
 * height.
 *
 * Loading model (kept deliberately simple): while the current `src` hasn't
 * decoded yet we show a spinner — the <svg> is rendered ONLY once the loaded
 * image matches the current src, so it never paints a stale frame (no
 * outline-jumps-ahead) and never gets stuck on the previous photo. The image is
 * pre-decoded via an Image() probe so that when the <svg>'s <image> renders, it
 * paints from cache immediately (image + outline appear together).
 *
 * Calls `onUnavailable` if the frame can't load (caller shows the crop), and
 * `onAspect` with the natural aspect once known.
 */
export function FrameWithPolygon({
  src,
  polygons,
  boxes,
  outlines,
  alt,
  onUnavailable,
  onAspect,
  strokeWidth = 2,
  holdPrevious = false,
  exactSource = false,
}: {
  src: string;
  polygons: number[][][];
  /** Optional detected-box overlay (#158, staff-only "рисовать боксы"):
   *  each box draws its rect plus TWO marked points — the center, and the
   *  bottom-mid "feet" point, which is what actually decides polygon
   *  membership in check_polygon_human_occupation
   *  (modules/occupancy_core/polygon_utils.py), not the center. */
  boxes?: FrameBox[];
  /** Контуры без заливки, зелёным — например полка на кропе стеллажа (калибровка). */
  outlines?: number[][][];
  alt: string;
  onUnavailable?: () => void;
  onAspect?: (ar: number) => void;
  strokeWidth?: number;
  /**
   * When the SAME logical stream just refreshes to a new frame (a dashboard tile
   * polling every few seconds), keep painting the last good frame until the new
   * one decodes instead of flashing the spinner — no blink. Leave OFF for the
   * lightbox, where `src` changes mean a DIFFERENT unit and a stale frame under
   * the new unit's outline would be wrong.
   */
  holdPrevious?: boolean;
  /** Live table proofs must never reuse another capture from the same camera path. */
  exactSource?: boolean;
}) {
  // Memory-cached frames resolve SYNCHRONOUSLY, so a tile remount (mobile
  // page flip) paints image+outline on the very first frame — no spinner
  // flash. A cache MISS still tries the painted-path memory: a re-minted
  // presign is the same picture under a new querystring, so keep painting the
  // previously decoded url while the fresh one loads and swaps in silently.
  const [loaded, setLoaded] = useState<{ src: string; w: number; h: number } | null>(() => {
    const hit = probeCachedImage(src);
    if (hit) return { src, w: hit.w, h: hit.h };
    return recallPainted(src, exactSource);
  });

  useEffect(() => {
    let alive = true;
    const probe = new Image();
    const done = () => {
      if (!alive) return;
      const w = probe.naturalWidth || 1;
      const h = probe.naturalHeight || 1;
      rememberPainted(src, w, h, exactSource);
      setLoaded({ src, w, h });
      onAspect?.(w / h);
    };
    probe.onload = done;
    probe.onerror = () => { if (alive) onUnavailable?.(); };
    probe.src = src;
    // Some browsers don't fire onload for an already-cached image — fire now.
    if (probe.complete && probe.naturalWidth) done();
    return () => { alive = false; };
    // onUnavailable/onAspect/polygons captured per render; keyed on src.
  }, [src]);

  const ready = loaded && loaded.src === src;
  // While the new src is still decoding, keep painting the last good frame when
  // holdPrevious is set (same-unit refresh) OR when it's provably the same
  // picture (same object path, re-minted querystring) — otherwise the spinner.
  const samePath = loaded && imagePathKey(loaded.src, exactSource) === imagePathKey(src, exactSource);
  const paint = ready ? loaded : !exactSource && (holdPrevious || samePath) ? loaded : null;
  return (
    <div className="relative size-full">
      {paint ? (
        <svg
          viewBox={`0 0 ${paint.w} ${paint.h}`}
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 h-full w-full"
          role="img"
          aria-label={alt}
        >
          <image
            href={paint.src}
            x={0}
            y={0}
            width={paint.w}
            height={paint.h}
            // A recalled stale-presign url can 403 if the browser evicted it —
            // drop to the loading state and let the fresh src take over. (Set
            // via ref: React only allows onError on HTML media tags.)
            ref={el => {
              if (!el) return;
              el.onerror = () => {
                if (paint.src !== src) setLoaded(l => (l && l.src === paint.src ? null : l));
              };
            }}
          />
          {polygons.map((poly, i) => (
            <polygon
              key={i}
              points={poly.map(([x, y]) => `${x * paint.w},${y * paint.h}`).join(' ')}
              fill="rgba(59,130,246,0.12)"
              stroke="#3b82f6"
              strokeWidth={strokeWidth}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {outlines?.map((poly, i) => (
            <polygon
              key={`o${i}`}
              points={poly.map(([x, y]) => `${x * paint.w},${y * paint.h}`).join(' ')}
              fill="none"
              stroke="#22c55e"
              strokeWidth={strokeWidth * 1.5}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {boxes?.map((b, i) => {
            const x = b.x0 * paint.w;
            const y = b.y0 * paint.h;
            const cx = ((b.x0 + b.x1) / 2) * paint.w;
            const cy = ((b.y0 + b.y1) / 2) * paint.h;
            const fx = cx;
            const fy = b.y1 * paint.h;
            return (
              <g key={i}>
                <rect
                  x={x}
                  y={y}
                  width={(b.x1 - b.x0) * paint.w}
                  height={(b.y1 - b.y0) * paint.h}
                  fill="none"
                  stroke={b.counted === false ? '#94a3b8' : '#22d3ee'}
                  strokeDasharray={b.counted === false ? '6 4' : undefined}
                  strokeWidth={strokeWidth}
                  vectorEffect="non-scaling-stroke"
                />
                {/* Center — reference point only, NOT what the occupancy check uses. */}
                <circle cx={cx} cy={cy} r={5} fill="#facc15" stroke="black" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                {/* Feet (bottom-mid) — the point check_polygon_human_occupation tests. */}
                <circle cx={fx} cy={fy} r={5} fill="#f43f5e" stroke="black" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              </g>
            );
          })}
        </svg>
      ) : (
        <DelayedSpinner />
      )}
    </div>
  );
}
